import type { OptimizedSplitResult, SavedProgramData, WorkoutPlanResult } from '../coachTypes';
import { api } from './api';

function normalizeExercise(ex: unknown): WorkoutPlanResult['days'][0]['exercises'][0] | null {
  if (!ex || typeof ex !== 'object') return null;
  const o = ex as Record<string, unknown>;
  const name = String(o.name ?? '').trim();
  if (!name) return null;
  return {
    name,
    sets: Math.max(1, Math.round(Number(o.sets) || 3)),
    reps: String(o.reps ?? '8–10'),
    notes: o.notes != null ? String(o.notes) : '',
  };
}

function normalizeDay(d: unknown): WorkoutPlanResult['days'][0] | null {
  if (!d || typeof d !== 'object') return null;
  const o = d as Record<string, unknown>;
  const day = String(o.day ?? '').trim() || 'Day';
  const focus = String(o.focus ?? '').trim();
  const exercises = Array.isArray(o.exercises)
    ? o.exercises.map(normalizeExercise).filter(Boolean) as WorkoutPlanResult['days'][0]['exercises']
    : [];
  return { day, focus, exercises };
}

export function normalizeWorkoutPlan(raw: unknown): WorkoutPlanResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.days) || o.days.length === 0) return null;
  const days = o.days.map(normalizeDay).filter(Boolean) as WorkoutPlanResult['days'];
  if (days.length === 0) return null;
  return {
    summary: String(o.summary ?? 'Training program').slice(0, 2000),
    split: String(o.split ?? 'Custom').slice(0, 200),
    days,
    progressionNotes: Array.isArray(o.progressionNotes)
      ? o.progressionNotes.map(String).filter(Boolean)
      : [],
  };
}

export function parseWorkoutPlanFromText(text: string): WorkoutPlanResult | null {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidates: string[] = [];
  if (fenced?.[1]) candidates.push(fenced[1].trim());
  const brace = text.match(/\{[\s\S]*"days"\s*:\s*\[[\s\S]*\}/);
  if (brace?.[0]) candidates.push(brace[0].trim());
  for (const raw of candidates) {
    try {
      const plan = normalizeWorkoutPlan(JSON.parse(raw));
      if (plan) return plan;
    } catch {
      /* try next */
    }
  }
  return parseWorkoutPlanFromMarkdown(text);
}

const DAY_HEADING =
  /^(?:#{1,3}\s*)?(?:\*\*)?(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Day\s*\d+)(?:\s*[—–-]\s*|\*\*\s*[—–-]\s*)([^*\n]+?)(?:\*\*)?\s*$/im;

const EXERCISE_LINE =
  /^[\s•\-*]*(.+?)\s*[—–-]\s*(\d+)\s*[x×]\s*([\d–\-]+(?:\s*reps?)?(?:\s*@\s*[\d.]+\s*(?:kg|lb|lbs))?)/i;

/** Parse coach markdown program replies (day headings + bullet exercises). */
export function parseWorkoutPlanFromMarkdown(text: string): WorkoutPlanResult | null {
  const lines = text.split('\n');
  const days: WorkoutPlanResult['days'] = [];
  let current: WorkoutPlanResult['days'][0] | null = null;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const dayMatch = line.match(DAY_HEADING);
    if (dayMatch) {
      if (current) days.push(current);
      current = {
        day: dayMatch[1]!.trim(),
        focus: dayMatch[2]!.trim(),
        exercises: [],
      };
      continue;
    }

    const exMatch = line.match(EXERCISE_LINE) ?? line.match(/^[\s•\-*]+(.+?)\s*[—–-]\s*(\d+)\s*[x×]\s*(.+)$/i);
    if (exMatch && current) {
      const name = exMatch[1]!.replace(/^\*\*|\*\*$/g, '').trim();
      if (name.length >= 2) {
        current.exercises.push({
          name,
          sets: Math.max(1, Number(exMatch[2]) || 3),
          reps: String(exMatch[3] ?? '8–10').trim(),
          notes: '',
        });
      }
    }
  }
  if (current) days.push(current);
  const exerciseCount = days.reduce((n, d) => n + d.exercises.length, 0);
  if (days.length < 1 || exerciseCount < 2) return null;

  const splitLine = text.match(/split:\s*(.+)/i)?.[1]?.trim();
  return {
    summary: 'Coach-built training program',
    split: splitLine?.slice(0, 200) ?? 'Custom',
    days,
    progressionNotes: [],
  };
}

export function formatPlanSummary(plan: WorkoutPlanResult): string {
  const lines = [
    plan.summary,
    '',
    `Split: ${plan.split}`,
    '',
    ...plan.days.map((d) => {
      const exLines =
        d.exercises.length > 0
          ? d.exercises.map((e) => `  • ${e.name} — ${e.sets}×${e.reps}`).join('\n')
          : '  • (No lifts listed — follow the focus for this day)';
      return `${d.day}${d.focus ? ` — ${d.focus}` : ''}\n${exLines}`;
    }),
  ];
  if (plan.progressionNotes.length) {
    lines.push('', 'Progression:', ...plan.progressionNotes.map((n) => `• ${n}`));
  }
  return lines.join('\n').trim();
}

export async function saveCoachProgram(
  plan: WorkoutPlanResult,
  split: OptimizedSplitResult | null,
  title?: string,
): Promise<{ id: string }> {
  const data: SavedProgramData = { plan, split, form: {} };
  const programTitle = (title ?? plan.split ?? 'Coach program').trim() || 'Coach program';
  const created = await api<{ id: string }>('/api/saved-workout-plans', {
    method: 'POST',
    body: JSON.stringify({ title: programTitle, data }),
  });
  await api('/api/profile/active-program', {
    method: 'PATCH',
    body: JSON.stringify({ planId: created.id }),
  });
  return created;
}
